import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { CreditLevelBadge } from '@/features/credit/components/CreditLevelBadge';
import { CreditLevel } from '@/types/credit.types';
import type { CreditProfile, ProfileSortBy, ProfileSortOrder } from '@/types/scoring.types';
import { formatCurrency } from '@/utils/formatters';

interface CandidatosListProps {
  profiles: CreditProfile[];
  selectedDirectoryId: string | null;
  sortBy: ProfileSortBy;
  sortOrder: ProfileSortOrder;
  onSelect: (directoryId: string) => void;
  onSortChange: (by: ProfileSortBy, order: ProfileSortOrder) => void;
}

export function CandidatosList({
  profiles,
  selectedDirectoryId,
  sortBy,
  sortOrder,
  onSelect,
  onSortChange,
}: CandidatosListProps) {
  function handleHeaderClick(key: ProfileSortBy) {
    if (key === sortBy) {
      onSortChange(key, sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      onSortChange(key, 'desc');
    }
  }

  function arrowFor(key: ProfileSortBy) {
    if (sortBy !== key) return <ChevronsUpDown size={11} className="opacity-50" />;
    return sortOrder === 'desc'
      ? <ChevronDown size={11} className="text-primary" />
      : <ChevronUp size={11} className="text-primary" />;
  }

  if (profiles.length === 0) {
    return (
      <p className="py-10 text-center text-sm text-muted-foreground">
        No hay candidatos que cumplan los filtros.
      </p>
    );
  }

  return (
    <div className="overflow-hidden">
      <div className="grid grid-cols-[1.5fr_0.6fr_0.7fr_0.7fr_0.6fr_0.4fr] gap-2 border-b border-border bg-muted/30 px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        <span>Nombre</span>
        <button
          onClick={() => handleHeaderClick('score')}
          className="flex items-center justify-end gap-1 hover:text-foreground"
        >
          Score {arrowFor('score')}
        </button>
        <span>Nivel</span>
        <button
          onClick={() => handleHeaderClick('income')}
          className="flex items-center justify-end gap-1 hover:text-foreground"
        >
          Ingreso {arrowFor('income')}
        </button>
        <button
          onClick={() => handleHeaderClick('credit')}
          className="flex items-center justify-end gap-1 hover:text-foreground"
        >
          Credito {arrowFor('credit')}
        </button>
        <span className="text-center">Eleg.</span>
      </div>

      <ul className="max-h-[640px] overflow-y-auto">
        {profiles.map((p) => {
          const selected = p.directory_id === selectedDirectoryId;
          return (
            <li
              key={p.directory_id}
              onClick={() => onSelect(p.directory_id)}
              className={[
                'grid cursor-pointer grid-cols-[1.5fr_0.6fr_0.7fr_0.7fr_0.6fr_0.4fr] gap-2 border-b border-border/50 px-3 py-2 text-xs transition-colors',
                selected
                  ? 'border-l-[3px] border-l-primary bg-primary/10 pl-[9px]'
                  : 'hover:bg-accent/30',
              ].join(' ')}
            >
              <span className="truncate font-medium text-foreground">
                {p.full_name || 'Pendiente'}
              </span>
              <span className="text-right tabular-nums text-foreground">{p.score_total ?? 0}</span>
              <span className="flex items-center gap-1">
                {Object.values(CreditLevel).includes(p.credit_level as CreditLevel) ? (
                  <CreditLevelBadge level={p.credit_level as CreditLevel} size="sm" />
                ) : (
                  <Badge variant="default">{p.credit_level}</Badge>
                )}
                {(p as { credit_level_source?: string }).credit_level_source === 'WHITELIST' && (
                  <span title="Nivel forzado desde Whitelist Oro (pruebas productivas), no proviene del score real">
                    <Badge variant="warning">Whitelist</Badge>
                  </span>
                )}
              </span>
              <span className="text-right tabular-nums text-foreground">
                {formatCurrency(p.monthly_income_mxn, 'MXN', 'es-MX')}
              </span>
              <span className="text-right tabular-nums text-foreground">
                {p.is_eligible === 'YES'
                  ? formatCurrency(p.max_credit_amount_mxn, 'MXN', 'es-MX')
                  : '—'}
              </span>
              <span
                className={[
                  'text-center text-[11px] font-bold',
                  p.is_eligible === 'YES' ? 'text-green-600' : 'text-red-500',
                ].join(' ')}
              >
                {p.is_eligible}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
